/**
 * Modal canônico de qualificação BANT.
 * Único formulário oficial — usado em LeadsPage (lista + detalhe) e OpportunityDetailPage.
 */
import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { useQualifyLead } from '@/hooks/useLeads'
import type { Lead, LeadQualify } from '@/types'

function getApiErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as { response?: { data?: { detail?: string } } }
  if (axiosErr?.response?.data?.detail) return axiosErr.response.data.detail
  return fallback
}

export function QualifyBantModal({ lead, onClose }: { lead: Lead; onClose: () => void }) {
  const mutation = useQualifyLead()
  const [form, setForm] = useState<LeadQualify>({
    budget_confirmed: lead.budget_confirmed || false,
    budget_range: lead.budget_range || '',
    authority_confirmed: lead.authority_confirmed || false,
    authority_contact_id: lead.authority_contact_id || '',
    need_confirmed: lead.need_confirmed || false,
    need_description: lead.need_description || '',
    timeline_confirmed: lead.timeline_confirmed || false,
    timeline_description: lead.timeline_description || '',
    notes: '',
  })
  const [error, setError] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const payload: LeadQualify = {
      ...form,
      budget_range: form.budget_range || null,
      authority_contact_id: form.authority_contact_id || null,
      need_description: form.need_description || null,
      timeline_description: form.timeline_description || null,
      notes: form.notes || null,
    }
    try { await mutation.mutateAsync({ leadId: lead.id, data: payload }); onClose() }
    catch (err: unknown) { setError(getApiErrorMessage(err, 'Erro ao salvar')) }
  }

  const bantScore = [form.budget_confirmed, form.authority_confirmed, form.need_confirmed, form.timeline_confirmed].filter(Boolean).length

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={onClose}>
      <div className="bg-card border rounded-lg shadow-lg w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold">Qualificação BANT</h2>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
        </div>
        <form onSubmit={handleSubmit} className="p-4 space-y-4 max-h-[70vh] overflow-y-auto">
          <div className="text-center">
            <div className="text-sm text-muted-foreground">{lead.company?.name}</div>
            <div className="text-2xl font-bold mt-1">{bantScore}/4</div>
            <div className="flex justify-center gap-1 mt-2">
              {['B', 'A', 'N', 'T'].map((l, i) => {
                const checks = [form.budget_confirmed, form.authority_confirmed, form.need_confirmed, form.timeline_confirmed]
                return <span key={l} className={`inline-flex items-center justify-center w-8 h-8 rounded-lg text-sm font-bold ${checks[i] ? 'bg-green-500 text-white' : 'bg-muted text-muted-foreground'}`}>{l}</span>
              })}
            </div>
          </div>
          {error && <div className="p-3 rounded bg-destructive/10 text-destructive text-sm">{error}</div>}
          <div className="space-y-2 p-3 rounded-lg bg-muted/30">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.budget_confirmed} onChange={(e) => setForm({ ...form, budget_confirmed: e.target.checked })} className="h-4 w-4 rounded" /><span className="font-medium text-sm">Budget (Orçamento)</span></label>
            {form.budget_confirmed && <Input placeholder="Faixa (ex: R$ 50k - 100k)" value={form.budget_range || ''} onChange={(e) => setForm({ ...form, budget_range: e.target.value })} />}
          </div>
          <div className="space-y-2 p-3 rounded-lg bg-muted/30">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.authority_confirmed} onChange={(e) => setForm({ ...form, authority_confirmed: e.target.checked })} className="h-4 w-4 rounded" /><span className="font-medium text-sm">Authority (Decisor)</span></label>
          </div>
          <div className="space-y-2 p-3 rounded-lg bg-muted/30">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.need_confirmed} onChange={(e) => setForm({ ...form, need_confirmed: e.target.checked })} className="h-4 w-4 rounded" /><span className="font-medium text-sm">Need (Necessidade)</span></label>
            {form.need_confirmed && <textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px]" placeholder="Descreva..." value={form.need_description || ''} onChange={(e) => setForm({ ...form, need_description: e.target.value })} />}
          </div>
          <div className="space-y-2 p-3 rounded-lg bg-muted/30">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.timeline_confirmed} onChange={(e) => setForm({ ...form, timeline_confirmed: e.target.checked })} className="h-4 w-4 rounded" /><span className="font-medium text-sm">Timeline (Prazo)</span></label>
            {form.timeline_confirmed && <Input placeholder="Ex: Q2 2026" value={form.timeline_description || ''} onChange={(e) => setForm({ ...form, timeline_description: e.target.value })} />}
          </div>
          <div><label className="text-sm font-medium">Observações</label><textarea className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[60px]" placeholder="Adicione observações sobre a qualificação..." value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            <Button type="submit" isLoading={mutation.isPending}>Salvar</Button>
          </div>
        </form>
      </div>
    </div>
  )
}
